import {
  BellIcon,
  CheckCircleIcon,
  CogIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiService, handleApiError } from "../services/api";

interface Settings {
  default_tickers: string[];
  default_analysis_type: string;
  email_notifications: boolean;
  slack_notifications: boolean;
  auto_refresh: boolean;
  refresh_interval: number;
  api_rate_limit: number;
  max_concurrent_analyses: number;
}

// Default settings (used as fallback)
const defaultSettings: Settings = {
  default_tickers: ["AAPL", "MSFT", "GOOGL"],
  default_analysis_type: "standard",
  email_notifications: true,
  slack_notifications: false,
  auto_refresh: true,
  refresh_interval: 300,
  api_rate_limit: 100,
  max_concurrent_analyses: 3,
};

export default function Settings() {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [newTicker, setNewTicker] = useState("");

  // Load settings from API
  const {
    data: loadedSettings,
    error: loadError,
    isLoading,
  } = useQuery({
    queryKey: ["settings"],
    queryFn: apiService.getSettings,
    retry: 2,
  });

  // Update local state when API data loads
  useEffect(() => {
    if (loadedSettings) {
      setSettings(loadedSettings);
    }
  }, [loadedSettings]);

  // Save settings to API
  const saveSettingsMutation = useMutation({
    mutationFn: (newSettings: Settings) => {
      return apiService.updateSettings(newSettings);
    },
    onSuccess: () => {
      console.log("Settings saved successfully");
    },
    onError: (error) => {
      console.error("Failed to save settings:", handleApiError(error));
    },
  });

  const handleSave = () => {
    saveSettingsMutation.mutate(settings);
  };

  const addTicker = () => {
    if (
      newTicker.trim() &&
      !settings.default_tickers.includes(newTicker.trim().toUpperCase())
    ) {
      setSettings({
        ...settings,
        default_tickers: [
          ...settings.default_tickers,
          newTicker.trim().toUpperCase(),
        ],
      });
      setNewTicker("");
    }
  };

  const removeTicker = (ticker: string) => {
    setSettings({
      ...settings,
      default_tickers: settings.default_tickers.filter((t) => t !== ticker),
    });
  };

  const updateSetting = (key: keyof Settings, value: any) => {
    setSettings({
      ...settings,
      [key]: value,
    });
  };

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {loadError && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-danger-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-danger-800">
                Failed to Load Settings
              </h4>
              <p className="mt-1 text-sm text-danger-700">
                {handleApiError(loadError)}
              </p>
              <p className="mt-1 text-xs text-danger-600">
                Using default settings. Your changes will be saved when the backend is available.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure your financial analysis preferences and system settings
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saveSettingsMutation.isPending || isLoading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saveSettingsMutation.isPending ? (
            <>
              <div className="spinner mr-2" />
              Saving...
            </>
          ) : isLoading ? (
            <>
              <div className="spinner mr-2" />
              Loading...
            </>
          ) : (
            <>
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Analysis Defaults */}
        <div className="card">
          <div className="flex items-center mb-4">
            <CogIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Analysis Defaults
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">Default Tickers</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {settings.default_tickers.map((ticker) => (
                  <span
                    key={ticker}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-800"
                  >
                    {ticker}
                    <button
                      onClick={() => removeTicker(ticker)}
                      className="ml-1 text-primary-600 hover:text-primary-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTicker}
                  onChange={(e) => setNewTicker(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addTicker()}
                  className="input flex-1"
                  placeholder="Add ticker (e.g., AAPL)"
                />
                <button onClick={addTicker} className="btn-secondary">
                  Add
                </button>
              </div>
            </div>

            <div>
              <label className="label">Default Analysis Type</label>
              <select
                value={settings.default_analysis_type}
                onChange={(e) =>
                  updateSetting("default_analysis_type", e.target.value)
                }
                className="select"
              >
                <option value="quick">Quick Analysis</option>
                <option value="standard">Standard Analysis</option>
                <option value="deep">Deep Analysis</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="card">
          <div className="flex items-center mb-4">
            <BellIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Email Notifications
                </p>
                <p className="text-xs text-gray-500">
                  Receive analysis reports via email
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={(e) =>
                    updateSetting("email_notifications", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Slack Notifications
                </p>
                <p className="text-xs text-gray-500">
                  Send alerts to Slack channels
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.slack_notifications}
                  onChange={(e) =>
                    updateSetting("slack_notifications", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Dashboard Settings */}
        <div className="card">
          <div className="flex items-center mb-4">
            <UserIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Dashboard</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Auto Refresh
                </p>
                <p className="text-xs text-gray-500">
                  Automatically refresh dashboard data
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_refresh}
                  onChange={(e) =>
                    updateSetting("auto_refresh", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div>
              <label className="label">Refresh Interval (seconds)</label>
              <input
                type="number"
                min="60"
                max="3600"
                value={settings.refresh_interval}
                onChange={(e) =>
                  updateSetting("refresh_interval", parseInt(e.target.value))
                }
                className="input"
              />
              <p className="mt-1 text-xs text-gray-500">
                How often to refresh data (60-3600 seconds)
              </p>
            </div>
          </div>
        </div>

        {/* System Settings */}
        <div className="card">
          <div className="flex items-center mb-4">
            <ShieldCheckIcon className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">System</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">API Rate Limit (requests/minute)</label>
              <input
                type="number"
                min="10"
                max="1000"
                value={settings.api_rate_limit}
                onChange={(e) =>
                  updateSetting("api_rate_limit", parseInt(e.target.value))
                }
                className="input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum API requests per minute
              </p>
            </div>

            <div>
              <label className="label">Max Concurrent Analyses</label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.max_concurrent_analyses}
                onChange={(e) =>
                  updateSetting(
                    "max_concurrent_analyses",
                    parseInt(e.target.value)
                  )
                }
                className="input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum number of analyses running simultaneously
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          System Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900">
              Frontend Version
            </p>
            <p className="text-lg font-semibold text-primary-600">1.0.0</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900">API Version</p>
            <p className="text-lg font-semibold text-primary-600">v1</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900">Last Updated</p>
            <p className="text-lg font-semibold text-primary-600">
              {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {saveSettingsMutation.isSuccess && (
        <div className="fixed bottom-4 right-4 bg-success-50 border border-success-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-success-600 mr-2" />
            <p className="text-sm font-medium text-success-800">
              Settings saved successfully!
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {saveSettingsMutation.isError && (
        <div className="fixed bottom-4 right-4 bg-danger-50 border border-danger-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-danger-600 mr-2" />
            <p className="text-sm font-medium text-danger-800">
              Failed to save settings: {handleApiError(saveSettingsMutation.error)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
